import { ExecutiveSummaryDetail } from '@containers/executive-summary/models/executive-summary-detail';
import { InformationMemorandumDetail } from '@containers/information-memorandum/models/information-memorandum-detail';
import { InformationMemorandumMenu } from '@containers/information-memorandum/models/information-memorandum-menu';
import { env } from '@core/env';
import { ClassicDIPOService } from '@core/services/classic-dipo.service';
import getExecutiveSummaryDefaultData, { ExecutiveSummaryDefaultData } from '@helpers/@utils/get-executive-summary-default-data';
import getInformationMemorandumDefaultData from '@helpers/@utils/get-information-memorandum-default-data';
import { BreadcrumbRouteProps } from '@mis/sushi-tailwind-react';
import { Profile } from '@model/profile';
import { MakeGenerics, Navigate, Route } from '@tanstack/react-location';

type Breadcrumb = BreadcrumbRouteProps & {
  next?: string | null;
  back?: string | null;
};

export type LocationGenericParams = {
  exSumId?: string;
  appId?: string;
  imId?: string;
  id?: string;
};

export type LocationGenericLoaderData = {
  profile: Profile;
  exSumDetail: ExecutiveSummaryDetail;
  imDetail: InformationMemorandumDetail;
  imMenu: InformationMemorandumMenu;
};

export type LocationGenerics = MakeGenerics<{
  Params: LocationGenericParams;
  RouteMeta: {
    breadcrumb: (params: LocationGenerics['Params'], isSubmitted: boolean) => Breadcrumb[];
  };
  LoaderData: LocationGenericLoaderData;
}>;

const routers: Route<LocationGenerics>[] = [
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
  {
    path: '/',
    element: () => import('./pages/landing').then((mod) => <mod.default />),
  },
  {
    path: 'executive/:exSumId/preview',
    element: () => import('./pages/executive/:exSumId/preview').then((mod) => <mod.default />),
    meta: {
      breadcrumb: ({ id, appId }, isSubmitted: boolean): Breadcrumb[] => {
        return [
          { label: 'Home', href: `${env.webURL}/dashboard` },
          { label: 'เอกสาร Filing', href: appId ? ClassicDIPOService.generateURLFiling(appId) : undefined },
          {
            label: 'reportNode.generateExecutiveSummary.title',
            href: isSubmitted ? undefined : `/executive/${id}/generateExecutiveSummary`,
            back: null,
          },
          { label: 'reportNode.preview.title' },
        ];
      },
    },
    loader: async ({ params: { exSumId } }: AnyValue): Promise<Partial<ExecutiveSummaryDefaultData>> => {
      return await getExecutiveSummaryDefaultData(exSumId);
    },
  },
  {
    path: 'executive/:exSumId',
    loader: async ({ params: { exSumId } }): Promise<Partial<ExecutiveSummaryDefaultData>> => {
      if (!exSumId) return {};
      return await getExecutiveSummaryDefaultData(exSumId);
    },
    meta: {
      breadcrumb: ({ appId }): Breadcrumb[] => {
        return [
          { label: 'Home', href: `${env.webURL}/dashboard` },
          { label: 'เอกสาร Filing', href: appId ? ClassicDIPOService.generateURLFiling(appId) : undefined },
        ];
      },
    },
    element: () => import('./pages/executive/:exSumId').then((mod) => <mod.default />),
    children: [
      {
        path: '/offeringInformation',
        element: () => import('./pages/executive/:exSumId/offeringInfo').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.offeringInformation.title' }];
          },
        },
      },
      {
        path: '/objective',
        element: () => import('./pages/executive/:exSumId/objectiveOfMoney').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.objective.title' }];
          },
        },
      },
      {
        path: '/dividendPolicy',
        element: () => import('./pages/executive/:exSumId/dividendPolicyAndPayment').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.dividendPolicy.title' }];
          },
        },
      },
      {
        path: '/issuerInformation',
        element: () => import('./pages/executive/:exSumId/offerorDetail').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.issuerInformation.title' }];
          },
        },
      },
      {
        path: '/majorShareholders',
        element: () => import('./pages/executive/:exSumId/majorShareholder').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.majorShareholders.title' }];
          },
        },
      },
      {
        path: '/revenueStructure',
        element: () => import('./pages/executive/:exSumId/incomeRatio').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.revenueStructure.title' }];
          },
        },
      },
      {
        path: '/boardOfDirectors',
        element: () => import('./pages/executive/:exSumId/boardOfDirectors').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.boardOfDirectors.title' }];
          },
        },
      },
      {
        path: '/riskFactors',
        element: () => import('./pages/executive/:exSumId/riskFactorSummary').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.riskFactors.title' }];
          },
        },
      },
      {
        path: '/financialStatus',
        element: () => import('./pages/executive/:exSumId/financialPositionAndPerformanceSummary').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [
              {
                label: 'reportNode.financialStatus.title',
                next: '/environmentalMgmt',
                back: null,
              },
            ];
          },
        },
      },
      {
        path: '/investorRelation',
        element: () => import('./pages/executive/:exSumId/investorRelations').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.investorRelation.title' }];
          },
        },
      },
      {
        path: '/additionalInformation',
        element: () => import('./pages/executive/:exSumId/additionalInformation').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.additionalInformation.title' }];
          },
        },
      },
      {
        path: '/setting',
        element: () => import('./pages/executive/:exSumId/setting').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.setting.title' }];
          },
        },
      },
      {
        path: '/generateExecutiveSummary',
        element: () => import('./pages/executive/:exSumId/executiveSummary').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.generateExecutiveSummary.title' }];
          },
        },
      },
    ],
  },
  {
    path: 'information-memorandum/:imId/preview',
    element: () => import('./pages/information-memorandum/:imId/preview').then((mod) => <mod.default />),
    meta: {
      breadcrumb: ({ companyNameTh }: AnyValue): Breadcrumb[] => {
        return [
          { label: 'Home', href: `${env.webURL}/dashboard` },
          { label: companyNameTh },
          { label: 'รายงานหลังการขายหลักทรัพย์' },
          { label: 'สรุปข้อสนเทศ' },
          { label: 'reportNode.previewIm.title' },
        ];
      },
    },
    loader: async ({ params: { imId } }): Promise<Partial<ExecutiveSummaryDefaultData>> => {
      if (!imId) return {};
      return await getInformationMemorandumDefaultData(imId);
    },
  },
  {
    path: 'information-memorandum/:imId',
    loader: async ({ params: { imId } }): Promise<Partial<ExecutiveSummaryDefaultData>> => {
      if (!imId) return {};
      return await getInformationMemorandumDefaultData(imId);
    },
    meta: {
      breadcrumb: ({ companyNameTh }: AnyValue): Breadcrumb[] => {
        return [
          { label: 'Home', href: `${env.webURL}/dashboard` },
          { label: companyNameTh },
          { label: 'รายงานหลังการขายหลักทรัพย์' },
          { label: 'สรุปข้อสนเทศ' },
        ];
      },
    },
    element: () => import('./pages/information-memorandum/:imId').then((mod) => <mod.default />),
    children: [
      {
        path: '/offeringInformation',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/offeringInfo').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.offeringInformation' }];
          },
        },
      },
      {
        path: '/objective',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/objectiveOfMoney').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.objective' }];
          },
        },
      },
      {
        path: '/dividendPolicy',
        element: () =>
          import('./pages/information-memorandum/:imId/executive-summary/dividendPolicyAndPayment').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.dividendPolicy' }];
          },
        },
      },
      {
        path: '/issuerInformation',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/offerorDetail').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.issuerInformation' }];
          },
        },
      },
      {
        path: '/subsidiaryInvestment',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/subsidiaryInvestment').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.subsidiaryInvestmentOptional' }];
          },
        },
      },
      {
        path: '/majorShareholders',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/majorShareholder').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.majorShareholders' }];
          },
        },
      },
      {
        path: '/revenueStructure',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/incomeRatio').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.revenueStructure' }];
          },
        },
      },
      {
        path: '/boardOfDirectors',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/boardOfDirectors').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.boardOfDirectors' }];
          },
        },
      },
      {
        path: '/riskFactors',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/riskFactorSummary').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.riskFactors' }];
          },
        },
      },
      {
        path: '/financialStatus',
        element: () =>
          import('./pages/information-memorandum/:imId/executive-summary/financialPositionAndPerformanceSummary').then((mod) => (
            <mod.default />
          )),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [
              {
                label: 'im.financialStatus',
              },
            ];
          },
        },
      },
      {
        path: '/investorRelation',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/investorRelations').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.investorRelation' }];
          },
        },
      },
      {
        path: '/otherRelatedParties',
        element: () => import('./pages/information-memorandum/:imId/executive-summary/otherRelatedParties').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.otherRelatedParties' }];
          },
        },
      },
      {
        path: '/additionalInformation',
        element: () =>
          import('./pages/information-memorandum/:imId/executive-summary/additionalInformation').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'im.additionalInformationOptional' }];
          },
        },
      },
      {
        path: '/publishIm',
        element: () => import('./pages/information-memorandum/:imId/publishIm').then((mod) => <mod.default />),
        meta: {
          breadcrumb: (): Breadcrumb[] => {
            return [{ label: 'reportNode.publishIm.title' }];
          },
        },
      },
    ],
  },
  {
    path: 'preview/information-memorandum/:imId',
    element: () => import('./pages/preview/information-memorandum/:imId').then((mod) => <mod.default />),
  },
  {
    path: 'preview',
    children: [
      {
        path: ':exsumId',
        element: () => import('./pages/preview/:exsumId').then((mod) => <mod.default />),
      },
      {
        path: 'set/:symbol',
        element: () => import('./pages/preview/set/:symbol').then((mod) => <mod.default />),
      },
      {
        path: 'sec/:secId/:version',
        element: () => import('./pages/preview/sec/:secId/:version').then((mod) => <mod.default />),
      },
    ],
  },
];

export default routers;
